import {
  INTRANET_CONTENT_KIND_LABELS,
  INTRANET_IMAGE_MAX_BYTES,
  IntranetContentKind,
  excerptFromIntranetBody,
  intranetContentIssues,
  intranetContentKindSchema,
  intranetContentPath,
  isIntranetLibraryKind,
  PermissionCode,
  type CreateIntranetContentInput,
  type IntranetAuthorGrantPerson,
  type IntranetContentDetail,
  type IntranetFeedItem,
  type IntranetHighlightSpan,
} from '@gb-ticket/shared-types';
import { ArrowLeftIcon, Loader2Icon } from 'lucide-react';
import { type FormEvent, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { usePageTitle } from '@/hooks/use-page-title';
import { ApiRequestError } from '@/lib/api';
import { fromDatetimeLocalValue, toDatetimeLocalValue } from '@/lib/format';
import { cn } from '@/lib/utils';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { hasPermission } from '@/platform/auth/permissions';
import { useMe } from '@/platform/auth/use-me';
import { useVoiceCopy } from '@/platform/voice/use-voice';
import { IntranetBodyEditor } from '@/products/intranet/components/intranet-body-editor';
import { intranetPanelClassName } from '@/products/intranet/features/intranet/intranet-panel';
import { publishAtFieldFromQuery } from '@/products/intranet/features/intranet/editorial-calendar';
import {
  IntranetComposerPreview,
  type IntranetLocalCover,
} from '@/products/intranet/components/intranet-composer-preview';
import {
  EMPTY_POLL_DRAFT,
  IntranetPollDraftFields,
  type IntranetPollDraft,
} from '@/products/intranet/components/intranet-poll';
import { IntranetPollEditor } from '@/products/intranet/components/intranet-poll-editor';
import {
  IntranetCoverField,
  type IntranetCoverDraft,
} from '@/products/intranet/components/intranet-cover-field';
import { IntranetHighlightControls } from '@/products/intranet/components/intranet-highlight-controls';
import type {
  IntranetAttachedFile,
  IntranetMediaRole,
} from '@/products/intranet/features/intranet/editor-media';
import {
  coverFailureMessage,
  coverFileIssue,
} from '@/products/intranet/features/intranet/cover-file';
import {
  useCreateIntranetContent,
  useIntranetAppearance,
  useIntranetPostingAuthors,
  useIntranetCategories,
  useIntranetContent,
  useIntranetDepartments,
  useSetIntranetPublished,
  useUpdateIntranetContent,
  useUploadIntranetCover,
} from '@/products/intranet/features/intranet/use-intranet';
import {
  uploadIntranetFile,
  useUploadIntranetFile,
} from '@/products/intranet/features/intranet/use-intranet-library';
import { useIntranetColleagues } from '@/products/intranet/features/intranet/use-intranet-directory';
import {
  useAssignIntranetLeadingCategory,
  useIntranetKnowledgeCategories,
  useIntranetKnowledgeSites,
} from '@/products/intranet/features/intranet/use-intranet-knowledge';
import { upsertIntranetPoll } from '@/products/intranet/features/intranet/use-intranet-poll';
import {
  INTRANET_EDITORIAL_PATH,
  INTRANET_ROOT,
  intranetEditPath,
} from '@/products/intranet/intranet-paths';

type FormState = {
  kind: IntranetContentKind;
  title: string;
  teaser: string;
  body: string;
  categoryId: string;
  leadingCategoryId: string;
  librarySortOrder: string;
  furtherCategoryIds: string[];
  siteIds: string[];
  targetAll: boolean;
  targetDepts: string[];
  requiresAcknowledgement: boolean;
  ackDeadline: string;
  commentsLocked: boolean;
  publishAt: string;
  expiresAt: string;
  notifyAudience: boolean;
  reviewDueAt: string;
  reviewerId: string;
  highlightSpan: IntranetHighlightSpan | null;
  highlightUntil: string;
};

function libraryKindFromQuery(value: string | null): IntranetContentKind {
  const parsed = intranetContentKindSchema.safeParse(value);
  if (parsed.success && isIntranetLibraryKind(parsed.data)) {
    return parsed.data;
  }
  return IntranetContentKind.NEWS;
}

const EMPTY_FORM: FormState = {
  kind: IntranetContentKind.NEWS,
  title: '',
  teaser: '',
  body: '',
  categoryId: '',
  leadingCategoryId: '',
  librarySortOrder: '0',
  furtherCategoryIds: [],
  siteIds: [],
  targetAll: true,
  targetDepts: [],
  requiresAcknowledgement: false,
  ackDeadline: '',
  commentsLocked: false,
  publishAt: '',
  expiresAt: '',
  notifyAudience: true,
  reviewDueAt: '',
  reviewerId: '',
  highlightSpan: null,
  highlightUntil: '',
};

function formFrom(content: IntranetContentDetail): FormState {
  return {
    kind: content.kind,
    title: content.title,
    teaser: content.teaser ?? '',
    body: content.body ?? '',
    categoryId: content.category?.id ?? '',
    leadingCategoryId: content.leadingCategory?.id ?? '',
    librarySortOrder: String(content.librarySortOrder ?? 0),
    furtherCategoryIds: (content.furtherCategories ?? [])
      .map((category) => category.id)
      .filter((id) => id !== content.leadingCategory?.id),
    siteIds: (content.sites ?? []).map((site) => site.id),
    targetAll: content.targetAll,
    targetDepts: content.targetDepts,
    requiresAcknowledgement: content.requiresAcknowledgement,
    ackDeadline: toDatetimeLocalValue(content.ackDeadline),
    commentsLocked: content.commentsLocked,
    publishAt: toDatetimeLocalValue(content.publishAt),
    expiresAt: toDatetimeLocalValue(content.expiresAt),
    reviewDueAt: toDatetimeLocalValue(content.reviewDueAt),
    reviewerId: content.reviewerId ?? '',
    notifyAudience: content.notifyAudience ?? true,
    highlightSpan: content.highlightSpan ?? null,
    highlightUntil: toDatetimeLocalValue(content.highlightUntil),
  };
}

function previewItem(
  form: FormState,
  existing: IntranetContentDetail | null,
  categories: readonly { id: string; name: string; slug: string }[],
  author: IntranetAuthorGrantPerson | null,
): IntranetFeedItem {
  const category = categories.find((entry) => entry.id === form.categoryId);
  const newsExcerpt =
    form.kind === IntranetContentKind.NEWS ? excerptFromIntranetBody(form.body) : null;
  return {
    id: existing?.id ?? '',
    kind: form.kind,
    title: form.title.trim() || 'Ohne Titel',
    slug: existing?.slug ?? '',
    teaser: newsExcerpt ? newsExcerpt.teaser : form.teaser.trim() || null,
    teaserHasMore: newsExcerpt?.hasMore ?? false,
    category: category ? { id: category.id, name: category.name, slug: category.slug } : null,
    targetAll: form.targetAll,
    targetDepts: form.targetDepts,
    isDraft: existing?.isDraft ?? true,
    publishedAt: existing?.publishedAt ?? null,
    requiresAcknowledgement: form.requiresAcknowledgement,
    ackDeadline: null,
    createdBy: author
      ? {
          id: author.id,
          displayName: author.displayName,
          profilePhotoUrl: author.profilePhotoUrl,
        }
      : (existing?.createdBy ?? { id: '', displayName: '' }),
    updatedAt: existing?.updatedAt ?? '',
    currentFile: null,
    feedback: existing?.feedback ?? { helpful: 0, notHelpful: 0 },
    coverUrl: existing?.coverUrl ?? null,
    coverAlt: existing?.coverAlt ?? null,
    coverScanStatus: existing?.coverScanStatus ?? null,
    coverMedia: existing?.coverMedia ?? null,
    pinned: existing?.pinned ?? false,
    likeCount: existing?.likeCount ?? 0,
    commentCount: existing?.commentCount ?? 0,
    viewCount: existing?.viewCount ?? 0,
    likedByMe: existing?.likedByMe ?? false,
    bookmarkedByMe: existing?.bookmarkedByMe ?? false,
  };
}

function pollIssue(draft: IntranetPollDraft): string | null {
  const question = draft.question.trim();
  const filled = draft.options.map((option) => option.trim()).filter(Boolean);
  const touched = question.length > 0 || draft.options.some((option) => option.trim().length > 0);
  if (!touched) {
    return null;
  }
  if (question.length < 3) {
    return 'Bitte eine Frage mit mindestens 3 Zeichen eingeben.';
  }
  if (filled.length < 2 || filled.length > 6) {
    return 'Eine Umfrage hat zwei bis sechs Antworten.';
  }
  return null;
}

function readyPoll(draft: IntranetPollDraft): { question: string; options: string[] } | null {
  if (pollIssue(draft)) {
    return null;
  }
  const question = draft.question.trim();
  const options = draft.options.map((option) => option.trim()).filter(Boolean);
  if (question.length < 3 || options.length < 2) {
    return null;
  }
  return { question, options };
}

function applyQueuedMedia(
  html: string,
  token: string,
  role: IntranetMediaRole,
  scanStatus: IntranetAttachedFile['scanStatus'],
  fileId: string,
): string {
  if (role === 'file') {
    return html;
  }
  if (scanStatus === 'BLOCKED') {
    return html.replace(
      new RegExp(`<span data-intranet-file="${token}"[^>]*>[\\s\\S]*?</span>`, 'g'),
      '',
    );
  }
  return html.replaceAll(token, fileId);
}

function requestFrom(form: FormState, authorUserId?: string): CreateIntranetContentInput {
  return {
    kind: form.kind,
    title: form.title.trim(),
    teaser: form.kind === IntranetContentKind.NEWS ? null : form.teaser.trim() || null,
    body: form.body.trim() ? form.body : null,
    categoryId: form.categoryId || null,
    leadingCategoryId: isIntranetLibraryKind(form.kind) ? form.leadingCategoryId || null : null,
    librarySortOrder: isIntranetLibraryKind(form.kind) ? Number(form.librarySortOrder) || 0 : 0,
    siteIds: isIntranetLibraryKind(form.kind) ? form.siteIds : [],
    targetAll: form.targetAll,
    targetDepts: form.targetAll ? [] : form.targetDepts,
    requiresAcknowledgement: form.requiresAcknowledgement,
    ackDeadline: form.requiresAcknowledgement
      ? (fromDatetimeLocalValue(form.ackDeadline) ?? null)
      : null,
    commentsLocked: form.commentsLocked,
    publishAt: fromDatetimeLocalValue(form.publishAt) ?? null,
    expiresAt: fromDatetimeLocalValue(form.expiresAt) ?? null,
    notifyAudience: form.notifyAudience,
    reviewDueAt:
      form.kind === IntranetContentKind.NEWS
        ? null
        : (fromDatetimeLocalValue(form.reviewDueAt) ?? null),
    reviewerId: form.kind === IntranetContentKind.NEWS ? null : form.reviewerId || null,
    highlightSpan: form.kind === IntranetContentKind.NEWS ? form.highlightSpan : null,
    highlightUntil:
      form.kind === IntranetContentKind.NEWS && form.highlightSpan === 'custom'
        ? (fromDatetimeLocalValue(form.highlightUntil) ?? null)
        : null,
    ...(authorUserId ? { authorUserId } : {}),
  };
}

/** One composer for creating and editing. Restriction follows `intranet.content:write`. */
export function ContentFormPage() {
  const { slug } = useParams();
  const existing = useIntranetContent(slug);
  usePageTitle(slug ? 'Beitrag bearbeiten' : 'Neuer Beitrag');

  if (slug && existing.isPending) {
    return (
      <p className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
        <Loader2Icon className="size-4 animate-spin" aria-hidden />
        Beitrag wird geladen …
      </p>
    );
  }
  if (slug && !existing.data) {
    return (
      <p role="alert" className="py-8 text-sm text-destructive">
        Der Beitrag konnte nicht geladen werden.
      </p>
    );
  }

  return <ContentForm key={slug ?? 'new'} existing={existing.data ?? null} />;
}

type QueuedMedia = { token: string; file: File; role: IntranetMediaRole };

function ContentForm({ existing }: { existing: IntranetContentDetail | null }) {
  const copy = useVoiceCopy();
  const navigate = useNavigate();
  const me = useMe();
  const [searchParams] = useSearchParams();
  const canWrite = hasPermission(me.data, PermissionCode.INTRANET_CONTENT_WRITE);
  const restricted = !canWrite;
  const requestedCategoryId = searchParams.get('category');
  const requestedPublishAt = publishAtFieldFromQuery(searchParams.get('publishAt'));
  const requestedKind = libraryKindFromQuery(searchParams.get('kind'));
  const [form, setForm] = useState<FormState>(
    existing
      ? formFrom(existing)
      : { ...EMPTY_FORM, publishAt: requestedPublishAt, kind: requestedKind },
  );
  const [error, setError] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(true);
  const coverDraft = useRef<IntranetCoverDraft | null>(null);
  const queued = useRef<QueuedMedia[]>([]);
  const [pollOpen, setPollOpen] = useState(false);
  const [pollDraft, setPollDraft] = useState<IntranetPollDraft>(EMPTY_POLL_DRAFT);
  const pollDraftRef = useRef(pollDraft);
  const [localCover, setLocalCover] = useState<(IntranetLocalCover & { file: File }) | null>(null);
  const postingAuthors = useIntranetPostingAuthors(existing == null);
  const [authorUserId, setAuthorUserId] = useState('');
  const categories = useIntranetCategories();
  const knowledgeCategories = useIntranetKnowledgeCategories(
    isIntranetLibraryKind(form.kind) && !restricted,
  );
  const assignCategories = useAssignIntranetLeadingCategory();
  const knowledgeSites = useIntranetKnowledgeSites();
  const availableCategories = useMemo(
    () =>
      restricted
        ? (categories.data?.items ?? []).filter((category) => category.canPost)
        : (categories.data?.items ?? []),
    [categories.data?.items, restricted],
  );
  const selectedAllowed = availableCategories.some((category) => category.id === form.categoryId);
  const requestedCategory = availableCategories.find(
    (category) => category.id === requestedCategoryId,
  );
  const restrictedCategoryId = selectedAllowed
    ? form.categoryId
    : (requestedCategory?.id ?? availableCategories[0]?.id ?? '');
  const effectiveForm = useMemo<FormState>(
    () =>
      restricted
        ? {
            ...form,
            kind: IntranetContentKind.NEWS,
            categoryId: restrictedCategoryId,
            targetAll: true,
            targetDepts: [],
            commentsLocked: false,
          }
        : form,
    [form, restricted, restrictedCategoryId],
  );
  const appearance = useIntranetAppearance();
  const departments = useIntranetDepartments(!restricted);
  const colleagues = useIntranetColleagues(
    {},
    !restricted && effectiveForm.kind !== IntranetContentKind.NEWS,
  );
  const create = useCreateIntranetContent();
  const update = useUpdateIntranetContent();
  const setPublished = useSetIntranetPublished();
  const uploadCover = useUploadIntranetCover();
  const uploadFile = useUploadIntranetFile(existing?.slug ?? '');
  const pending =
    create.isPending ||
    update.isPending ||
    setPublished.isPending ||
    uploadCover.isPending ||
    assignCategories.isPending ||
    (restricted && (categories.isPending || availableCategories.length === 0));
  const isDraft = existing?.isDraft ?? true;
  const isNews = effectiveForm.kind === IntranetContentKind.NEWS;
  const showNewsPreview = showPreview && isNews;

  function pickLocalCover(file: File | null) {
    setLocalCover((current) => {
      if (current?.file === file) {
        return current;
      }
      if (current) {
        URL.revokeObjectURL(current.src);
      }
      if (!file || typeof URL.createObjectURL !== 'function') {
        return null;
      }
      return {
        file,
        src: URL.createObjectURL(file),
        media: file.type.startsWith('video/') ? 'video' : 'image',
      };
    });
  }

  function patch<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  async function attachMedia(file: File, role: IntranetMediaRole): Promise<IntranetAttachedFile> {
    if (!existing) {
      const token = crypto.randomUUID();
      queued.current.push({ token, file, role });
      return {
        id: token,
        filename: file.name,
        mimeType: file.type || 'application/octet-stream',
        scanStatus: 'PENDING',
      };
    }
    const saved = await uploadFile.mutateAsync({ file });
    return {
      id: saved.id,
      filename: saved.filename,
      mimeType: saved.mimeType,
      scanStatus: saved.scanStatus,
    };
  }

  function toggleDepartment(id: string, checked: boolean) {
    setForm((current) => ({
      ...current,
      targetDepts: checked
        ? [...current.targetDepts, id]
        : current.targetDepts.filter((entry) => entry !== id),
    }));
  }

  const meId = me.data?.id ?? '';
  const authorChoices = postingAuthors.data?.authors ?? [];
  const showAuthorChoice = existing == null && authorChoices.length > 1;
  const selectedAuthor =
    authorChoices.find((person) => person.id === (authorUserId || meId)) ??
    authorChoices.find((person) => person.id === meId) ??
    null;
  const authorForRequest =
    showAuthorChoice && selectedAuthor && selectedAuthor.id !== meId
      ? selectedAuthor.id
      : undefined;

  async function save(publish: boolean) {
    const body = requestFrom(effectiveForm, authorForRequest);
    if (
      restricted &&
      (body.kind !== IntranetContentKind.NEWS ||
        !availableCategories.some((category) => category.id === body.categoryId))
    ) {
      const message = copy('intranet.editor.channel-required');
      setError(message);
      toast.error(message);
      return;
    }
    const issue =
      body.title.length < 3
        ? 'Bitte einen Titel mit mindestens 3 Zeichen eingeben.'
        : intranetContentIssues(body)[0]?.message;
    if (issue) {
      setError(issue);
      return;
    }
    const pollProblem =
      !existing && pollOpen && form.kind === IntranetContentKind.NEWS
        ? pollIssue(pollDraftRef.current)
        : null;
    if (pollProblem) {
      setError(pollProblem);
      return;
    }
    const imageMaxBytes = appearance.data?.imageMaxBytes ?? INTRANET_IMAGE_MAX_BYTES;
    const chosenCover = coverDraft.current;
    if (chosenCover && effectiveForm.kind === IntranetContentKind.NEWS) {
      const issue = coverFileIssue(chosenCover.file, imageMaxBytes);
      if (issue) {
        setError(issue);
        toast.error(issue);
        return;
      }
    }
    setError(null);
    try {
      const saved = existing
        ? await update.mutateAsync({ slug: existing.slug, body })
        : await create.mutateAsync(body);
      if (isIntranetLibraryKind(saved.kind)) {
        await assignCategories.mutateAsync({
          slug: saved.slug,
          body: {
            leadingCategoryId: effectiveForm.leadingCategoryId || null,
            furtherCategoryIds: effectiveForm.furtherCategoryIds.filter(
              (id) => id !== effectiveForm.leadingCategoryId,
            ),
          },
        });
      }
      let bodyHtml = form.body;
      for (const item of queued.current) {
        try {
          const uploaded = await uploadIntranetFile(saved.slug, item.file);
          bodyHtml = applyQueuedMedia(
            bodyHtml,
            item.token,
            item.role,
            uploaded.scanStatus,
            uploaded.id,
          );
        } catch (caught) {
          toast.error(
            caught instanceof ApiRequestError ? caught.message : 'Upload fehlgeschlagen.',
          );
        }
      }
      queued.current = [];
      if (bodyHtml !== form.body) {
        await update.mutateAsync({
          slug: saved.slug,
          body: requestFrom({ ...effectiveForm, body: bodyHtml }),
        });
      }
      const draft = coverDraft.current;
      if (draft && saved.kind === IntranetContentKind.NEWS) {
        try {
          await uploadCover.mutateAsync({
            slug: saved.slug,
            file: draft.file,
            alt: draft.alt.trim(),
          });
          coverDraft.current = null;
        } catch (caught) {
          const message = coverFailureMessage(caught, imageMaxBytes);
          const text = existing ? message : `${message} Der Beitrag wurde als Entwurf gespeichert.`;
          setError(text);
          toast.error(text);
          if (!existing) {
            void navigate(intranetEditPath(saved.slug));
          }
          return;
        }
      }
      const pollBody = !existing ? readyPoll(pollDraftRef.current) : null;
      if (pollBody && saved.kind === IntranetContentKind.NEWS) {
        try {
          await upsertIntranetPoll(saved.slug, pollBody);
        } catch (caught) {
          toast.error(
            caught instanceof ApiRequestError ? caught.message : 'Upload fehlgeschlagen.',
          );
        }
      }
      if (publish && saved.isDraft) {
        const released = await setPublished.mutateAsync({
          slug: saved.slug,
          published: true,
          notifyAudience: form.notifyAudience,
        });
        if (released.highlightNotice === 'full') {
          toast.success('Veröffentlicht. Das Highlight wurde nicht gesetzt, die Leiste ist voll.');
        } else if (released.highlightNotice === 'past') {
          toast.success(
            'Veröffentlicht. Das Highlight wurde nicht gesetzt, der Zeitpunkt liegt in der Vergangenheit.',
          );
        } else {
          toast.success(released.isDraft ? 'Veröffentlichung geplant.' : 'Beitrag veröffentlicht.');
        }
      } else {
        toast.success('Beitrag gespeichert.');
      }
      void navigate(intranetContentPath({ kind: saved.kind, slug: saved.slug }));
    } catch (caught) {
      const message =
        caught instanceof ApiRequestError ? caught.message : 'Speichern ist fehlgeschlagen.';
      setError(message);
      toast.error(message);
    }
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    void save(false);
  }

  const selectedCategory = availableCategories.find(
    (category) => category.id === effectiveForm.categoryId,
  );
  const backHref =
    restricted && selectedCategory
      ? `${INTRANET_ROOT}?category=${encodeURIComponent(selectedCategory.slug)}`
      : restricted
        ? INTRANET_ROOT
        : INTRANET_EDITORIAL_PATH;

  return (
    <section className="space-y-6">
      <Button asChild variant="ghost" size="sm" className="-ml-2">
        <Link to={backHref}>
          <ArrowLeftIcon aria-hidden />
          {restricted ? 'Zum Channel' : 'Zur Redaktion'}
        </Link>
      </Button>
      <PlatformPageHeader
        title={existing ? 'Beitrag bearbeiten' : 'Neuer Beitrag'}
        description={
          existing && !isDraft
            ? 'Veröffentlicht — Änderungen sind nach dem Speichern sofort sichtbar.'
            : restricted
              ? copy('intranet.editor.draft-private')
              : 'Entwürfe sieht nur die Redaktion.'
        }
      />

      {restricted && !categories.isPending && availableCategories.length === 0 ? (
        <p
          role="alert"
          className="rounded-md border border-destructive/40 p-3 text-sm text-destructive"
        >
          {copy('intranet.editor.no-channel')}
        </p>
      ) : null}

      <form onSubmit={onSubmit} noValidate>
        <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
          <div
            className={cn(
              'grid items-start gap-6 p-4 sm:p-6',
              showNewsPreview && 'lg:grid-cols-[minmax(0,1fr)_minmax(16rem,24rem)]',
            )}
          >
            <div className="min-w-0 space-y-6">
              {existing?.submittedBy ? (
                <p className="text-sm text-muted-foreground">
                  Angezeigt als {existing.createdBy.displayName}. Erstellt von{' '}
                  {existing.submittedBy.displayName}.
                </p>
              ) : null}
              {showAuthorChoice ? (
                <div className="space-y-2">
                  <Label htmlFor="intranet-author">Beitrag erstellen als</Label>
                  <Select value={selectedAuthor?.id ?? meId} onValueChange={setAuthorUserId}>
                    <SelectTrigger id="intranet-author" className="w-full max-w-md">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {authorChoices.map((person) => {
                        const detail = person.departmentName ?? person.jobTitle;
                        return (
                          <SelectItem key={person.id} value={person.id}>
                            {detail ? `${person.displayName} · ${detail}` : person.displayName}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              ) : null}
              {isNews ? (
                <IntranetCoverField
                  content={existing?.kind === IntranetContentKind.NEWS ? existing : null}
                  onDraftChange={(draft) => {
                    coverDraft.current = draft;
                    pickLocalCover(draft?.file ?? null);
                  }}
                />
              ) : null}

              <div className="space-y-2">
                <Label htmlFor="intranet-title">Titel</Label>
                <Input
                  id="intranet-title"
                  value={form.title}
                  maxLength={200}
                  required
                  onChange={(event) => patch('title', event.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label id="intranet-body-label">Text</Label>
                <IntranetBodyEditor
                  labelId="intranet-body-label"
                  value={form.body}
                  title={form.title}
                  teaser={form.teaser}
                  slug={existing?.slug}
                  onChange={(html) => patch('body', html)}
                  onApplyDraft={(suggestion) => {
                    setForm((current) => ({
                      ...current,
                      title: suggestion.title?.trim() ? suggestion.title : current.title,
                      teaser: suggestion.teaser ?? current.teaser,
                    }));
                  }}
                  {...(isNews
                    ? {
                        onAttach: (file: File, role: IntranetMediaRole) => attachMedia(file, role),
                        ...(!restricted ? { onOpenPoll: () => setPollOpen(true) } : {}),
                      }
                    : {})}
                />
                {isNews && pollOpen && !restricted ? (
                  existing ? (
                    <IntranetPollEditor slug={existing.slug} />
                  ) : (
                    <IntranetPollDraftFields
                      draft={pollDraft}
                      onChange={(next) => {
                        pollDraftRef.current = next;
                        setPollDraft(next);
                      }}
                    />
                  )
                ) : null}
                {isNews ? (
                  <p className="text-xs text-muted-foreground">
                    Die Kurzfassung entsteht aus den ersten vier Zeilen.
                  </p>
                ) : null}
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="intranet-kind">Art</Label>
                  <Select
                    value={effectiveForm.kind}
                    disabled={restricted}
                    onValueChange={(value) => {
                      const parsed = intranetContentKindSchema.safeParse(value);
                      if (parsed.success) {
                        patch('kind', parsed.data);
                      }
                    }}
                  >
                    <SelectTrigger id="intranet-kind" className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(restricted
                        ? [IntranetContentKind.NEWS]
                        : intranetContentKindSchema.options
                      ).map((kind) => (
                        <SelectItem key={kind} value={kind}>
                          {INTRANET_CONTENT_KIND_LABELS[kind]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="intranet-category">Channel</Label>
                  <Select
                    value={effectiveForm.categoryId || (!restricted ? 'none' : undefined)}
                    onValueChange={(value) => patch('categoryId', value === 'none' ? '' : value)}
                  >
                    <SelectTrigger id="intranet-category" className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {!restricted ? <SelectItem value="none">Ohne Channel</SelectItem> : null}
                      {availableCategories.map((category) => (
                        <SelectItem key={category.id} value={category.id}>
                          {category.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {isIntranetLibraryKind(effectiveForm.kind) ? (
                  <div className="space-y-2">
                    <Label htmlFor="intranet-leading-category">Führende Kategorie</Label>
                    <Select
                      value={effectiveForm.leadingCategoryId || 'none'}
                      onValueChange={(value) => {
                        const leadingCategoryId = value === 'none' ? '' : value;
                        setForm((current) => ({
                          ...current,
                          leadingCategoryId,
                          furtherCategoryIds: current.furtherCategoryIds.filter(
                            (id) => id !== leadingCategoryId,
                          ),
                        }));
                      }}
                    >
                      <SelectTrigger id="intranet-leading-category" className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Ohne Kategorie</SelectItem>
                        {(knowledgeCategories.data?.items ?? []).map((category) => (
                          <SelectItem key={category.id} value={category.id}>
                            {category.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : null}
                {isIntranetLibraryKind(effectiveForm.kind) && !effectiveForm.leadingCategoryId ? (
                  <div className="space-y-2">
                    <Label htmlFor="intranet-library-order">Reihenfolge ohne Kategorie</Label>
                    <Input
                      id="intranet-library-order"
                      inputMode="numeric"
                      value={effectiveForm.librarySortOrder}
                      onChange={(event) => patch('librarySortOrder', event.target.value)}
                    />
                  </div>
                ) : null}
                {isIntranetLibraryKind(effectiveForm.kind) ? (
                  <>
                    <fieldset className="space-y-2 sm:col-span-2">
                      <legend className="text-sm font-medium">Weitere Kategorien</legend>
                      <p className="text-sm text-muted-foreground">
                        Dieselbe Seite, ohne sie zu kopieren. Die führende Kategorie bleibt die
                        Freigabe.
                      </p>
                      <div className="grid gap-1 sm:grid-cols-2">
                        {(knowledgeCategories.data?.items ?? [])
                          .filter((category) => category.id !== effectiveForm.leadingCategoryId)
                          .map((category) => (
                            <label
                              key={category.id}
                              className="flex min-h-11 items-center gap-3 text-sm"
                            >
                              <Checkbox
                                checked={effectiveForm.furtherCategoryIds.includes(category.id)}
                                onCheckedChange={(checked) =>
                                  setForm((current) => ({
                                    ...current,
                                    furtherCategoryIds:
                                      checked === true
                                        ? [...new Set([...current.furtherCategoryIds, category.id])]
                                        : current.furtherCategoryIds.filter(
                                            (id) => id !== category.id,
                                          ),
                                  }))
                                }
                              />
                              {category.name}
                            </label>
                          ))}
                      </div>
                    </fieldset>
                    <fieldset className="space-y-2">
                      <legend className="text-sm font-medium">Standortgültigkeit</legend>
                      <label className="flex min-h-11 items-center gap-3 text-sm">
                        <Checkbox
                          checked={effectiveForm.siteIds.length === 0}
                          onCheckedChange={(checked) => {
                            if (checked === true) {
                              patch('siteIds', []);
                            }
                          }}
                        />
                        Standortübergreifend
                      </label>
                      {[
                        ...(knowledgeSites.data?.sites ?? []),
                        ...(existing?.sites ?? []).filter(
                          (site) =>
                            !site.isActive &&
                            effectiveForm.siteIds.includes(site.id) &&
                            !(knowledgeSites.data?.sites ?? []).some(
                              (active) => active.id === site.id,
                            ),
                        ),
                      ].map((site) => (
                        <label key={site.id} className="flex min-h-11 items-center gap-3 text-sm">
                          <Checkbox
                            checked={effectiveForm.siteIds.includes(site.id)}
                            onCheckedChange={(checked) => {
                              const next = new Set(effectiveForm.siteIds);
                              if (checked === true) {
                                next.add(site.id);
                              } else {
                                next.delete(site.id);
                              }
                              patch('siteIds', [...next]);
                            }}
                          />
                          {site.name}
                          {'isActive' in site && site.isActive === false ? ' (inaktiv)' : ''}
                        </label>
                      ))}
                    </fieldset>
                  </>
                ) : null}
              </div>

              {!restricted ? (
                <fieldset className={`${intranetPanelClassName} space-y-3`}>
                  <legend className="px-1 text-sm font-medium">Zielgruppe</legend>
                  <label className="flex min-h-11 items-center gap-3 text-sm">
                    <Checkbox
                      checked={form.targetAll}
                      onCheckedChange={(checked) => patch('targetAll', checked === true)}
                    />
                    Alle Mitarbeiter
                  </label>
                  {!form.targetAll ? (
                    <div className="grid gap-1 sm:grid-cols-2">
                      {(departments.data?.items ?? []).map((department) => (
                        <label
                          key={department.id}
                          className="flex min-h-11 items-center gap-3 text-sm"
                        >
                          <Checkbox
                            checked={form.targetDepts.includes(department.id)}
                            onCheckedChange={(checked) =>
                              toggleDepartment(department.id, checked === true)
                            }
                          />
                          {department.name}
                        </label>
                      ))}
                    </div>
                  ) : null}
                </fieldset>
              ) : null}

              <fieldset className={`${intranetPanelClassName} space-y-3`}>
                <legend className="px-1 text-sm font-medium">Optionen</legend>
                <label className="flex min-h-11 items-center gap-3 text-sm">
                  <Checkbox
                    checked={form.requiresAcknowledgement}
                    onCheckedChange={(checked) =>
                      patch('requiresAcknowledgement', checked === true)
                    }
                  />
                  Lesebestätigung verlangen
                </label>
                {form.requiresAcknowledgement ? (
                  <div className="space-y-2 sm:max-w-xs">
                    <Label htmlFor="intranet-ack-deadline">Frist</Label>
                    <Input
                      id="intranet-ack-deadline"
                      type="datetime-local"
                      value={form.ackDeadline}
                      onChange={(event) => patch('ackDeadline', event.target.value)}
                    />
                  </div>
                ) : null}
                <label className="flex min-h-11 items-center gap-3 text-sm">
                  <Checkbox
                    checked={form.notifyAudience}
                    onCheckedChange={(checked) => patch('notifyAudience', checked === true)}
                  />
                  Zielgruppe benachrichtigen
                </label>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="intranet-publish-at">Veröffentlichen am</Label>
                    <Input
                      id="intranet-publish-at"
                      type="datetime-local"
                      value={form.publishAt}
                      onChange={(event) => patch('publishAt', event.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="intranet-expires-at">Läuft aus am</Label>
                    <Input
                      id="intranet-expires-at"
                      type="datetime-local"
                      value={form.expiresAt}
                      onChange={(event) => patch('expiresAt', event.target.value)}
                    />
                  </div>
                </div>
                {!isNews ? (
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="intranet-review-due">Prüffrist</Label>
                      <Input
                        id="intranet-review-due"
                        type="datetime-local"
                        value={form.reviewDueAt}
                        onChange={(event) => patch('reviewDueAt', event.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="intranet-reviewer">Zuständige Person</Label>
                      <Select
                        value={form.reviewerId || 'none'}
                        onValueChange={(value) =>
                          patch('reviewerId', value === 'none' ? '' : value)
                        }
                      >
                        <SelectTrigger id="intranet-reviewer" className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Verfasserin oder Verfasser</SelectItem>
                          {(colleagues.data?.items ?? []).map((person) => (
                            <SelectItem key={person.id} value={person.id}>
                              {person.displayName}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ) : null}
                {!restricted ? (
                  <label className="flex min-h-11 items-center gap-3 text-sm">
                    <Checkbox
                      checked={form.commentsLocked}
                      onCheckedChange={(checked) => patch('commentsLocked', checked === true)}
                    />
                    Kommentare schließen
                  </label>
                ) : null}
              </fieldset>

              {!isNews ? (
                <div className="space-y-2">
                  <Label htmlFor="intranet-teaser">Kurzfassung</Label>
                  <Textarea
                    id="intranet-teaser"
                    rows={2}
                    maxLength={500}
                    value={form.teaser}
                    onChange={(event) => patch('teaser', event.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Erscheint in der Liste und in der Mitteilung.
                  </p>
                </div>
              ) : null}
            </div>

            {showNewsPreview ? (
              <aside
                aria-label="Vorschau"
                className="min-w-0 lg:sticky lg:top-20 lg:border-l lg:border-border lg:pl-6"
              >
                <IntranetComposerPreview
                  item={previewItem(
                    effectiveForm,
                    existing,
                    categories.data?.items ?? [],
                    showAuthorChoice ? selectedAuthor : null,
                  )}
                  body={form.body}
                  localCover={localCover}
                  pollDraft={!existing && pollOpen ? pollDraft : null}
                  savedPollSlug={
                    existing?.kind === IntranetContentKind.NEWS ? existing.slug : undefined
                  }
                />
              </aside>
            ) : null}
          </div>

          {error ? (
            <p
              role="alert"
              className="border-t border-border px-4 pb-2 text-sm text-destructive sm:px-6"
            >
              {error}
            </p>
          ) : null}

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border bg-muted/30 px-4 py-4 sm:px-6">
            {isNews ? (
              <label className="flex items-center gap-2 text-sm font-medium">
                <Switch checked={showPreview} onCheckedChange={setShowPreview} />
                Vorschau
              </label>
            ) : (
              <span />
            )}
            <div className="ml-auto flex flex-wrap justify-end gap-2">
              {isDraft ? (
                <>
                  <Button type="button" disabled={pending} onClick={() => void save(true)}>
                    {pending ? <Loader2Icon className="animate-spin" aria-hidden /> : null}
                    Erstellen
                  </Button>
                  <Button type="submit" variant="outline" disabled={pending}>
                    Als Entwurf speichern
                  </Button>
                </>
              ) : (
                <Button type="submit" disabled={pending}>
                  {pending ? <Loader2Icon className="animate-spin" aria-hidden /> : null}
                  Änderungen speichern
                </Button>
              )}
              {form.kind === IntranetContentKind.NEWS && !restricted ? (
                <IntranetHighlightControls
                  slug={existing?.slug}
                  kind={form.kind}
                  pinned={Boolean(existing?.pinned)}
                  highlightSpan={form.highlightSpan}
                  highlightUntil={
                    form.highlightSpan === 'custom'
                      ? (fromDatetimeLocalValue(form.highlightUntil) ?? null)
                      : (existing?.highlightUntil ?? null)
                  }
                  persist={existing ? 'api' : 'form'}
                  onFormChange={(span, until) =>
                    setForm((current) => ({
                      ...current,
                      highlightSpan: span,
                      highlightUntil: until ? toDatetimeLocalValue(until) : '',
                    }))
                  }
                />
              ) : null}
            </div>
          </div>
        </div>
      </form>
    </section>
  );
}
